import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Menu, MenuContent, MenuDivider, MenuItem, MenuTrigger } from '@/components/molecules/Menu';
import {
    type FishGroupDetail,
    findLatestRevertibleOperation,
    useGetFishGroupEvents,
} from '@/domain/fishGroups';

import CloseFishGroupModal from './CloseFishGroupModal';
import DiscardFishGroupModal from './DiscardFishGroupModal';
import OperateFishGroupModal from './operations/OperateFishGroupModal';
import RecordFishGroupEventModal from './RecordFishGroupEventModal';
import RelocateFishGroupModal from './RelocateFishGroupModal';
import RevertOperationModal from './revert/RevertOperationModal';

import styles from './actionModals.module.scss';

interface Props {
    clientId: number;
    detail: FishGroupDetail;
    /** Whether the FG has any incoming/outgoing transfers (controls discard visibility). */
    hasTransfers: boolean;
}

type OpenAction = 'relocate' | 'close' | 'discard' | 'recordEvent' | 'operate' | 'revert' | null;

/**
 * Lifecycle / composition actions for the fish-group detail modal, collected
 * under a single "Actions ▾" dropdown.
 *
 * Why a dropdown and not a row of buttons: the previous design had a
 * primary "Close" button next to the modal's own X close — operators
 * misread "Close" as "close the modal", which was the wrong action and a
 * one-way one. Routing every lifecycle action through a labelled menu
 * forces each entry to describe its full intent ("Close fish group",
 * "Discard fish group") and makes the destructive ones reachable only
 * after an explicit second click, not as a single prominent button.
 */
export default function FishGroupActionsBar(props: Props) {
    const { clientId, detail, hasTransfers } = props;
    const { t } = useTranslation('fishGroups');
    const [open, setOpen] = useState<OpenAction>(null);
    // The same query the Events tab reads, so it is usually cached already.
    const { data: events } = useGetFishGroupEvents(clientId, detail.id, undefined, {
        enabled: detail.status === 'active',
    });

    if (detail.status !== 'active') return null;

    // Offered from the group itself only for the operation its history ends on;
    // older ones are reachable from the Events tab, where the backend explains
    // what blocks them.
    const lastOperation = findLatestRevertibleOperation(events);

    // The operate wizard only makes sense when the FG has an open location:
    // both the source picker (intra-location filter) and the destination
    // picker (location's cages) need a location id.
    const canOperate = detail.currentLocation !== null;
    const operateInitialFishGroup =
        detail.currentLocation !== null
            ? {
                  id: detail.id,
                  name: detail.name,
                  currentCageId: detail.currentLocation.cageId,
                  locationId: detail.currentLocation.locationId,
                  locationName: detail.currentLocation.locationName,
              }
            : null;

    const handleOpen = (action: NonNullable<OpenAction>) => setOpen(action);

    return (
        <div className={styles.actionsMenu}>
            <Menu>
                <MenuTrigger variant="secondary" caret>
                    {t('actions.menu.trigger')}
                </MenuTrigger>
                <MenuContent align="end" width="wide">
                    {canOperate && (
                        <MenuItem onClick={() => handleOpen('operate')}>
                            {t('actions.menu.runOperation')}
                        </MenuItem>
                    )}
                    <MenuItem onClick={() => handleOpen('relocate')}>
                        {t('actions.menu.relocate')}
                    </MenuItem>
                    <MenuItem onClick={() => handleOpen('recordEvent')}>
                        {t('actions.menu.recordEvent')}
                    </MenuItem>
                    <MenuDivider />
                    {lastOperation && (
                        <MenuItem danger onClick={() => handleOpen('revert')}>
                            {t('actions.menu.revert')}
                        </MenuItem>
                    )}
                    {!hasTransfers && (
                        <MenuItem danger onClick={() => handleOpen('discard')}>
                            {t('actions.menu.discard')}
                        </MenuItem>
                    )}
                    <MenuItem danger onClick={() => handleOpen('close')}>
                        {t('actions.menu.close')}
                    </MenuItem>
                </MenuContent>
            </Menu>

            <RelocateFishGroupModal
                clientId={clientId}
                fishGroupId={detail.id}
                fishGroupName={detail.name}
                currentCageId={detail.currentLocation?.cageId ?? null}
                currentLocation={detail.currentLocation}
                isOpen={open === 'relocate'}
                onClose={() => setOpen(null)}
            />
            <CloseFishGroupModal
                clientId={clientId}
                fishGroupId={detail.id}
                fishGroupName={detail.name}
                currentLocation={detail.currentLocation}
                isOpen={open === 'close'}
                onClose={() => setOpen(null)}
            />
            {!hasTransfers && (
                <DiscardFishGroupModal
                    clientId={clientId}
                    fishGroupId={detail.id}
                    fishGroupName={detail.name}
                    currentLocation={detail.currentLocation}
                    isOpen={open === 'discard'}
                    onClose={() => setOpen(null)}
                />
            )}
            <RecordFishGroupEventModal
                clientId={clientId}
                fishGroupId={detail.id}
                fishGroupName={detail.name}
                currentLocation={detail.currentLocation}
                isOpen={open === 'recordEvent'}
                onClose={() => setOpen(null)}
            />
            <OperateFishGroupModal
                clientId={clientId}
                initialFishGroup={operateInitialFishGroup}
                isOpen={open === 'operate'}
                onClose={() => setOpen(null)}
            />
            {lastOperation && open === 'revert' && (
                <RevertOperationModal
                    clientId={clientId}
                    operationId={lastOperation.id}
                    isOpen
                    onClose={() => setOpen(null)}
                />
            )}
        </div>
    );
}
